import React, { useEffect, useMemo, useState } from "react";
import DeckGL from "@deck.gl/react";
import { ArcLayer, ScatterplotLayer } from "@deck.gl/layers";
import { Map as MapGL } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { fetchDemoNationalOdm } from "../../services/demoApi";
import MapAttribution from "../../components/common/MapAttribution";
import { basemapStyle } from "../../config/theme";
import { useThemeColors } from "../../hooks/useThemeColors";

const INITIAL_VIEW = { longitude: 25.6, latitude: 58.8, zoom: 6.4, pitch: 35, bearing: 0 };

interface FlowRow {
  origin_cell: string; dest_cell: string; trips: number;
  o_lat: number; o_lon: number; o_city: string;
  d_lat: number; d_lon: number; d_city: string;
}

const NationalOdmDemo: React.FC = () => {
  const colors = useThemeColors();
  const [rows, setRows] = useState<FlowRow[]>([]);
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [detail, setDetail] = useState("");

  useEffect(() => {
    fetchDemoNationalOdm<{ rows?: FlowRow[]; error?: string; detail?: string }>()
      .then((d) => {
        if (d.error) throw new Error(d.detail || d.error);
        setRows(d.rows || []);
        setStatus("ok");
      })
      .catch((e) => { setDetail(String(e.message || e)); setStatus("error"); });
  }, []);

  // unique cells (for the dot layer) + headline stats
  const { cells, total } = useMemo(() => {
    const m = new Map<string, { lon: number; lat: number; city: string }>() as any;
    let t = 0;
    for (const r of rows) {
      t += r.trips;
      m.set(r.origin_cell, { lon: r.o_lon, lat: r.o_lat, city: r.o_city });
      m.set(r.dest_cell, { lon: r.d_lon, lat: r.d_lat, city: r.d_city });
    }
    return { cells: Array.from(m.values()), total: t };
  }, [rows]);

  const interCity = useMemo(() => rows.filter((r) => r.origin_cell !== r.dest_cell), [rows]);
  const maxTrips = useMemo(() => Math.max(1, ...interCity.map((r) => r.trips)), [interCity]);

  const layers = [
    new ScatterplotLayer({
      id: "cells",
      data: cells,
      getPosition: (d: any) => [d.lon, d.lat],
      getRadius: 1400,
      radiusMinPixels: 2,
      getFillColor: [...colors.rgb("series-10"), 160],
    }),
    new ArcLayer({
      id: "flows",
      data: interCity,
      getSourcePosition: (d: FlowRow) => [d.o_lon, d.o_lat],
      getTargetPosition: (d: FlowRow) => [d.d_lon, d.d_lat],
      getSourceColor: colors.rgb("series-2"),
      getTargetColor: colors.rgb("series-3"),
      getWidth: (d: FlowRow) => 1 + 7 * Math.sqrt(d.trips / maxTrips),
      getHeight: 0.5,
      greatCircle: false,
      opacity: 0.55,
      pickable: true,
    }),
  ];

  return (
    <div className="relative h-screen w-screen">
      <DeckGL
        initialViewState={INITIAL_VIEW}
        controller={true}
        layers={layers}
        getTooltip={({ object }: any) =>
          object && object.origin_cell
            ? `${object.o_city || object.origin_cell} → ${object.d_city || object.dest_cell}\n${Math.round(
                object.trips
              ).toLocaleString()} trips/day`
            : null
        }
      >
        <MapGL mapStyle={basemapStyle(colors.dark)} attributionControl={false} />
      </DeckGL>
      <MapAttribution />

      <div className="absolute top-4 left-4 max-w-xs rounded-lg bg-surface/90 p-4 shadow-lg backdrop-blur">
        <h1 className="text-base font-semibold text-ink">National Origin–Destination</h1>
        <p className="mt-1 text-xs text-ink-muted">
          Synthetic national flows (gravity model) — <span className="font-medium">illustrative</span>, generated from
          census land-use. Real Tartu telco data folds in next.
        </p>
        {status === "loading" && <p className="mt-3 text-sm text-ink-muted">Loading flows…</p>}
        {status === "error" && (
          <p className="mt-3 text-sm text-bad-ink">Couldn’t reach demo API.<br />
            <span className="text-xs">{detail}</span></p>
        )}
        {status === "ok" && (
          <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
            <dt className="text-ink-muted">Flows drawn</dt><dd className="text-right font-medium">{interCity.length.toLocaleString()}</dd>
            <dt className="text-ink-muted">Trips/day shown</dt><dd className="text-right font-medium">{Math.round(total).toLocaleString()}</dd>
            <dt className="text-ink-muted">Cells</dt><dd className="text-right font-medium">{cells.length.toLocaleString()}</dd>
          </dl>
        )}
      </div>
    </div>
  );
};

export default NationalOdmDemo;
